// FEAT-022: SMARTDent Standalone License 역할 헬퍼
// 스펙 §3.3 확정 기준. FE role은 UI 렌더 전용 — 서버 인가 대체 불가.
//
// user.companyInfo: user 내부 중첩 객체 (AuthReducer L49 참조)
// user.isTypeChange: true = 딜러 계정 (스펙 §3.3 구현 확정 기준)
// user.dealerType: 'internal'|'external'|undefined — 신규 필드 (FEAT-025)
//
// 판별 우선순위: HQ org → Branch org → dealerType/isTypeChange 유저 플래그 → 딜러 org 코드 → Subsidiary

import * as Sentry from '@sentry/react';
import { ORG_HQ, ORG_SUBSIDIARY, ROLE_INTERNAL_DEALER, ROLE_EXTERNAL_DEALER } from '../config/appConstants';
// U-55: 날짜 표시 포맷 SSoT(IMP-438 locale 기준 포함)는 dateDisplay.js.
import { formatDisplayDateTime, formatDisplayDate } from './dateDisplay';
// sk 포맷 SSoT — 3-seg 받은 레코드 판정(BUG-206 R2: strict 추출기와 tier 판정의 경계 단일화).
import { isReceivedAllocationSk } from './rayteamsTables';

// 딜러 계열 org 전체(Dst*/SubDst*/Distributor) — FEAT-050 S1.
// company.org 기반 딜러 org 판별에 사용. isTypeChange 미설정 회사 계정에 대한 fallback.
// 규칙: HQ=본사 / Branch(+Subsidiary)=법인 / 나머지(본 Set)=딜러.
const DEALER_ORG_CODES = new Set([
    'Distributor', 'DstHQ', 'DstBranch', 'DstHQBranch', 'SubDstDst', 'SubDstDstBranch',
]);

// IMP-437: RayCodes.CompanyTypes[].title 은 영어 리터럴이라 그대로 렌더하면 19개 언어에서
// 미번역 노출된다. org code → i18n 키 매핑 SSoT. LicenseDealerTab.js / LicenseSubsidiaryTab.js
// 두 탭이 각자 `ORG_TITLE` 을 정의하던 중복을 여기로 통합한다(두 번째 사용처가 이미 있으므로
// 추출이 정당 — CLAUDE.md 중복 금지). RayCodes.CompanyTypes 구조(code/title) 자체는 다른 화면
// (회사 관리 등)이 영어 title 을 그대로 쓰므로 변경하지 않는다.
export const ORG_TYPE_LABEL_KEYS = {
    Branch: 'company.orgType.branch',
    Distributor: 'company.orgType.distributor',
    DstBranch: 'company.orgType.dstBranch',
    DstHQ: 'company.orgType.dstHQ',
    DstHQBranch: 'company.orgType.dstHQBranch',
    [ORG_HQ]: 'company.orgType.hq',
    SubDstDst: 'company.orgType.subDstDst',
    SubDstDstBranch: 'company.orgType.subDstDstBranch',
};

// 매핑에 있으면 translate(key), 없으면 orgCode 원문 반환 — 기존 `ORG_TITLE[code] || code`
// 폴백 동작을 그대로 보존한다(fail-loud, silent-drop 아님).
export const resolveOrgTypeLabel = (orgCode, translate) => {
    if (!orgCode) return null;
    const key = ORG_TYPE_LABEL_KEYS[orgCode];
    if (key && typeof translate === 'function') return translate(key);
    return orgCode;
};

// 회사 필드({ org, dealerType, isTypeChange }) → 역할 판정 SSoT.
// getLicenseRole(사용자)과 isLicenseCorpCompany(회사)가 같은 규칙을 쓰도록 판정부만 공개로 분리.
export const resolveLicenseRoleByCompanyFields = ({ org, dealerType, isTypeChange } = {}) => {
    if (org === ORG_HQ) return ORG_HQ;
    // Branch org: dealerType=external은 라우트 제한 유지(ExternalDealer), 나머지는 Subsidiary.
    // isTypeChange=true 가 있더라도 Branch org는 InternalDealer 로 분류하지 않음.
    // 판별 우선순위: HQ → Branch(external 분기) → dealerType/isTypeChange → 딜러 org 코드 → Subsidiary
    if (org === 'Branch') {
        if (dealerType === 'external') return ROLE_EXTERNAL_DEALER;
        return ORG_SUBSIDIARY;
    }
    // dealerType='external' 은 isTypeChange 와 무관하게 ExternalDealer 로 분류.
    // (운영 데이터에 isTypeChange 미설정 + dealerType='external' 케이스 존재 — admin
    // 메뉴/검색/필터 등 권한외 UI 노출 차단을 위해 dealerType 단독 인정.)
    if (dealerType === 'external') return ROLE_EXTERNAL_DEALER;
    if (isTypeChange) {
        return ROLE_INTERNAL_DEALER;  // 'internal' 또는 undefined(legacy). FEAT-025에서 dealerType 필수화 예정.
    }
    if (DEALER_ORG_CODES.has(org)) return ROLE_INTERNAL_DEALER;
    return ORG_SUBSIDIARY;
};

export const getLicenseRole = (user) => {
    if (!user || !user.companyId) return null;
    return resolveLicenseRoleByCompanyFields({
        org: user.companyInfo?.org,
        dealerType: user.dealerType,
        isTypeChange: user.isTypeChange,
    });
};

// 회사 레코드가 법인(ORG_SUBSIDIARY 역할)인가 — 그 회사 소속 사용자의 getLicenseRole 결과와 같은 기준.
// 회사 레코드의 dealerType/isTypeChange 를 사용자 플래그 자리에 대입한다.
export const isLicenseCorpCompany = (company) => (
    Boolean(company?.companyId)
    && resolveLicenseRoleByCompanyFields(company) === ORG_SUBSIDIARY
);

// sk 포맷: "info:{corpId}:{dealerId}" — 세 번째 세그먼트가 dealerId.
// 포맷이 다르거나 없으면 sk 원본을 그대로 반환하여 호출부가 fallback 처리 가능.
export const extractDealerIdFromSk = (sk) => {
    if (!sk || typeof sk !== 'string') return null;
    const parts = sk.split(':');
    return parts[2] || sk;
};

// PR #224 review (Security Major #12): IDOR 가드 전용 strict variant.
//   fallback `parts[2] || sk` 가 sk 원본을 반환하여 가드 우회 가능 (fallback 통과).
//   security-sensitive 호출자(LicenseDealerTab/DealerIssuePanel SEC-006 가드)는 본 함수 사용.
//
// BUG-206 R2 (PR #543 리뷰 Minor): 포맷 검증을 sk 포맷 SSoT(`rayteamsTables.isReceivedAllocationSk`)
// 에 위임한다. 종전에는 이 함수(`startsWith('info:')` + 3 segments)와 스코프 tier 판정 predicate 가
// 각각 형식을 정의해 `info::{id}`(mg 세그먼트 빈 문자열)에서 답이 갈렸다 — 한쪽은 유효 3-seg, 다른
// 쪽은 무효. 같은 sk 에 대해 IDOR 가드와 tier 판정이 다른 결론을 내지 않도록 경계를 하나로 둔다.
// 손상 mg 는 무효(null) 로 통일 — `isCorpLedgerSk` 가 이미 취한 fail-closed 방향과 동일하다.
export const extractDealerIdFromSkStrict = (sk) => (
    isReceivedAllocationSk(sk) ? String(sk).split(':')[2] : null
);

// U-55: 날짜 표시 포맷은 src/lib/dateDisplay.js 로 승격했다. 기존 이름은 호출처 변경 없이
// 쓰도록 같은 함수를 그대로 재노출한다(시그니처·동작 동일).
export const formatLicenseDate = formatDisplayDateTime;
export const formatLicenseDateOnly = formatDisplayDate;

// 역할별 탭 가시성 — 스펙 §3.3 매트릭스의 SSoT.
// LicenseHub / Tab / Modal 에서 모두 이 predicate만 참조하도록 단일화.
// 딜러(Internal/External) 는 SMARTDent Standalone 권한만 보유하므로 전사 라이선스 Overview 미노출.
// PR #191 review (Code Quality Minor) — Memo 글자 수 단일 source. 액션과 모달 양쪽에서
// import. DDB item 400KB 한도 안전 마진 + UI 표시 합리성 기준.
export const MEMO_MAX_LEN = 500;

// IMP-171: PC rtid/guid 단축 표시. LicenseDetailModal 인라인 구현
// (`rtid.length > 12 ? rtid.slice(0, 8) + '…' : rtid`) 을 헬퍼로 추출 — license activity log /
// audit history / device guid 등 재사용 대비.
//   - PC_GUID_VISIBLE_LEN: 마스킹 시 노출할 앞 글자 수(기존 slice(0, 8)).
//   - 마스킹 임계: 노출부 + 생략부호가 원본보다 충분히 짧을 때만 단축 → visibleLen + 4
//     (기본 8 → 12, 기존 `length > 12` 동작 보존). 비문자열/빈 값은 '' 반환.
export const PC_GUID_VISIBLE_LEN = 8;
export const maskPcGuid = (guid, visibleLen = PC_GUID_VISIBLE_LEN) => {
    if (typeof guid !== 'string' || !guid) return '';
    return guid.length > visibleLen + 4
        ? `${guid.slice(0, visibleLen)}…`
        : guid;
};

// IMP-168: company 매칭 실패 시 raw 식별자(내부 SK / UUID / legacy) UI 노출 방어.
// 표준 dealerId 형식(대문자 prefix + 숫자, 예: RAY90095) 만 그대로 노출하고,
// 그 외 형식은 앞 N자 truncate 하여 민감 식별자 표면화를 차단(CWE-200 defense-in-depth).
const DEALER_ID_PATTERN = /^[A-Z]+[0-9]+$/; // 내부 전용 — 공개 API 표면 최소화(review #358 Arch).
export const COMPANY_FALLBACK_VISIBLE_LEN = 8; // 비표준 식별자 노출 허용 길이 (maskPcGuid 선례와 동일 패턴).
export const isStandardDealerId = (id) => typeof id === 'string' && DEALER_ID_PATTERN.test(id);
export const formatCompanyFallbackId = (id) => {
    // 표시용 id 는 string 이 전제. 비문자열/빈 값은 truncate 없이 그대로 반환(상위 호출부 가드 위임).
    if (typeof id !== 'string' || !id) return id;
    return isStandardDealerId(id) ? id : `${id.slice(0, COMPANY_FALLBACK_VISIBLE_LEN)}…`;
};

// IMP-166: "회사명 (id)" 인라인 라벨 포맷 SSoT. 원래 components/license/CompanyCell.js 에
// 있었으나, IMP-424 리팩터로 licenseHelper 내부(resolveOtherLicenseInfo)에서도 재사용하며
// CompanyCell → licenseHelper 기존 import(formatCompanyFallbackId) 와의 순환 의존을 피하기
// 위해 이곳으로 이전. CompanyCell.js 는 기존 소비처 호환을 위해 그대로 re-export.
// 인자 순서는 CompanyCell 의 id-우선 규약과 통일(id, name) — 호출부 swap 실수 방지.
export const formatCompanyInlineLabel = (id, name) => (name ? `${name} (${id})` : id);

// IMP-153: License 식별자 표시 fallback — LicenseDetailModal + CustomerLicensesTable 공유.
// PR #215 review (Arch Minor). whitespace-only 거부(.trim() 가드, IMP-150 정책).
// sk fallback 미포함 — 내부 schema 노출 방지. 우선순위: groupName → userid → userId → '-'.
const trimmedDisplay = (s) => (typeof s === 'string' && s.trim() ? s.trim() : null);
export const getFriendlyName = (lic) =>
    trimmedDisplay(lic?.groupName) || trimmedDisplay(lic?.userid) || trimmedDisplay(lic?.userId) || '-';
export const getUserEmail = (lic) => trimmedDisplay(lic?.email);

// IMP-229: userid/userId 소스 경계 casing-agnostic 접근자 (검색/actor-매칭 전용).
// DDB rayteams-license → `userid`(소문자), MongoDB voucher_license·RAYTeams user → `userId`(camelCase).
// dealerCustomerLicenses 에 두 소스가 혼재하므로 원시 식별자를 casing 무관하게 읽는다.
// groupName/email fallback 은 호출부 책임. 본 헬퍼는 전역 SSoT 가 아니라 범위 한정 접근자다
// (커버 2곳: customerLicenseFilter 검색 / LicenseDetailModal actor 매칭).
// [미사용 규칙] priority·trim·리터럴 fallback 시맨틱이 다른 경로는 본 헬퍼로 병합 금지 —
//   getFriendlyName(위) / resolveOtherLicenseInfo name chain / usePcActivation / LicenseAction write-path.
export const getLicenseUserId = (lic) => lic?.userid || lic?.userId || null;

// IMP-155: license expired 판정 SSoT. 표시: CustomerLicensesTable.computeLicStatus / LicenseDetailModal statusKey +
// 양 탭('expired' 필터: LicenseSubsidiaryTab/DealerIssuePanel) 의 동일 인라인 규칙 통합.
// expired 는 number ms timestamp 이며 now 이하면 만료(경계 포함 — 기존 동작 보존).
// now 파라미터는 테스트 경계값 주입용. 기본 Date.now() — 런타임 동작 동일.
export const isExpired = (lic, now = Date.now()) =>
    typeof lic?.expired === 'number' && lic.expired <= now;

// BUG-170: SMARTDent 할당 대상이 법인(Branch/Subsidiary)인지 판별하는 SSoT.
// 할당은 대상을 딜러 레코드(info:{mg}:{dealerId})에 기록하는데, 대상이 법인이면
// 그 법인은 로그인 시 자기 corp 레코드(info:{자기mg})를 읽으므로 이 할당을 볼 수
// 없다 (§7 "성공 응답인데 수신자에겐 silent 무시" 방지 — AllocateToDealer 가드에 사용).
// getCompanyTypeBadge(AllocateDealerModal)의 법인 분류와 동일 신호.
// 우선순위: falsy → false / 딜러(dealerType internal|external) → false / 법인(org==='Branch'
// 또는 isTypeChange===false) → true / 그 외 → false.
export const isSubsidiaryCompany = (company) => {
    if (!company) return false;
    if (company.dealerType === 'internal' || company.dealerType === 'external') return false;
    if (DEALER_ORG_CODES.has(company.org)) return false; // FEAT-050 S1: 딜러 org 은 법인 아님(역할/분류 정합)
    if (company.org === 'Branch' || company.isTypeChange === false) return true;
    return false;
};

// FEAT-050 S2: 루트 회사 판정 SSoT. parentCompanyId 가 ''·null·부재(undefined) 모두 루트.
// (RAY90096 등 최상위 실데이터는 parentCompanyId 필드 자체가 없음 — === '' 만으론 놓침)
export const isRootCompany = (company) => !company?.parentCompanyId;

export const canViewSubsidiary = (role) => role === ORG_HQ || role === ORG_SUBSIDIARY;
export const canViewIssuance = (role) => role === ORG_HQ || role === ORG_SUBSIDIARY;
export const canViewDealer = (role) => role === ORG_SUBSIDIARY || role === ROLE_INTERNAL_DEALER || role === ROLE_EXTERNAL_DEALER;
export const isDealerRole = (role) => role === ROLE_INTERNAL_DEALER || role === ROLE_EXTERNAL_DEALER;

// IMP-425: 할당 목록에서 자기 행(sk 말단 === companyId)을 제외한다. §12.1-A self 정책 술어 SSoT.
// companyId 부재 시 fail-closed 로 빈 목록을 반환하지 않고 원본을 그대로 돌려주면 self 행이
// 통과하므로, 호출부가 companyId 를 보장하도록 명시적으로 빈 배열을 반환한다.
export const excludeSelfAllocationRow = (rows, companyId) => {
    if (!Array.isArray(rows)) return [];
    if (!companyId) return [];
    return rows.filter((d) => extractDealerIdFromSk(d.sk) !== companyId);
};

// BUG-098 PR #203: voucher_license 종류 파생 SSOT.
// RTSQA-339 round 9 (2026-05-14): Confluence "Local License Check API doc" (2045050896)
//   에 따라 SK 5번째 segment (`:` 구분, 0-indexed=4) 가 type 의 가장 신뢰할 수 있는 SSoT.
//   sk 형식: `lcuse:region:groupId:userId:type:guid` — type ∈ {'initial', 'renewal'}.
//   기존 lic.type / lic.token fallback 은 voucher_license MongoDB 컬렉션의 잘못 저장
//   사례(전체가 'renewal' prefix 로 저장)로 false-positive 판정 회귀 — SK 기반 우선 판정.
// 우선순위:
//   1) lic.sk 의 5번째 segment ('initial' / 'renewal' 명시) — SSoT (가장 신뢰)
//   2) lic.type === 'renewal' / 'renew' — voucher_license 명시 필드
//   3) lic.token?.startsWith('renewal-') — token prefix fallback (legacy)
// legacy 'renew' 타입도 renewal 로 처리 (AC-5).
// TODO(BUG-098): BE 신규 발급 시 type='renewal' 일관 저장 + 기존 'renew'/type 누락 레코드
//   마이그레이션 완료 후 'renew' 분기 및 token prefix fallback 제거 예정.
// PR #241 review (Sec minor): 알려진 SK type segment 화이트리스트 — schema drift
// 감지용. dev 환경에서만 console.warn (production silent fallback 유지).
const KNOWN_SK_TYPES = new Set(['initial', 'renewal', 'renew']);

export const isRenewalLicense = (lic) => {
    // (1) SK 5번째 segment 우선 (Confluence 2045050896 SSoT).
    if (typeof lic?.sk === 'string') {
        const parts = lic.sk.split(':');
        if (parts.length >= 5) {
            // PR #241 review (Sec minor, CWE-697): case-insensitive 비교 —
            // BE 의 대소문자 일관성 보장 부재 시 false-negative 차단.
            const skType = String(parts[4]).toLowerCase();
            if (skType === 'renewal' || skType === 'renew') return true;
            if (skType === 'initial') return false;
            // SK 의 type segment 가 알 수 없는 값이면 schema drift 감지 — dev 환경
            // 한정 로깅 (production 은 silent fallback chain 으로 계속 진행).
            // 빈 segment 는 warn 제외 (5번째 segment 누락 케이스와 동일 처리).
            if (skType.length > 0 && !KNOWN_SK_TYPES.has(skType)
                && typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('[isRenewalLicense] unknown SK type segment:', skType);
            }
        }
    }
    // (2) lic.type 명시 필드 — legacy 'renew' 도 renewal 로 처리.
    if (lic?.type === 'renewal' || lic?.type === 'renew') return true;
    // (3) token prefix fallback.
    return Boolean(lic?.token?.startsWith('renewal-'));
};

// AC-3 (IMP-151): MatchVoucherToRayTeamsUser thunk → 순수 매처 추출.
// sk 우선 → user+group fallback. 부수효과 없음(순수). drift 관측은 reportVoucherSsotDrift 분리.
export const matchVoucherToRayTeamsUser = (rayteamsUser, dealerCustomerLicenses, localLicenseSk = null) => {
    if (!rayteamsUser || !Array.isArray(dealerCustomerLicenses)) return null;
    const userGroupMatched = (v) =>
        v?.userId === rayteamsUser.userId && v?.groupId === rayteamsUser.groupId;
    let matched = null;
    if (typeof localLicenseSk === 'string' && localLicenseSk.length > 0) {
        matched = dealerCustomerLicenses.find((v) => userGroupMatched(v) && v?.sk === localLicenseSk) ?? null;
    }
    if (!matched) matched = dealerCustomerLicenses.find(userGroupMatched) ?? null;
    return matched;
};

// AC-3 (IMP-151): SSoT drift 관측(부수효과) — matched 존재 + eligibility code=N0003 불일치 시 warn+breadcrumb.
// PII 비노출(PR #342): matchedSk + code 만 기록. 매처를 순수 유지하기 위해 분리.
// 메시지 문자열은 기존 thunk 와 동일 유지(회귀 가드 호환).
export const reportVoucherSsotDrift = (matched, eligibilityCode) => {
    if (!(matched && eligibilityCode === 'N0003')) return;
    const driftMsg =
        '[MatchVoucherToRayTeamsUser] SSoT drift: matched voucher exists but eligibility code=N0003 (no registered license)';
    const driftData = { matchedSk: matched?.sk, code: eligibilityCode };
    console.warn(driftMsg, driftData);
    Sentry.addBreadcrumb({
        category: 'license.ssot-drift',
        level: 'warning',
        message: driftMsg,
        data: driftData,
    });
};

// ─── IMP-226: resolveOtherLicenseInfo ────────────────────────────────────────
// PR #244 R1 Arch Major M-2 (P1) — PCActivationPanel.otherLicenseInfo useMemo 의
// 4책임 로직을 순수 함수로 추출. input → output 검증으로 source regex 의존 줄이고
// 회귀 차단성 향상. PCActivationPanel 은 useMemo 래핑 + deps 명시만 담당.
//
// 책임:
//   1. localLicenseStatusSk pattern sanity check (lcuse: prefix).
//   2. name lookup chain: externalVoucherMeta > skMatched > matchedVoucher > rayteamsUser.
//   3. typeLabel — skMatched isRenewalLicenseFn 우선, fallback sk 5번째 segment.
//   4. dealer 2-분기:
//      - externalVoucherMeta 경로 (BE meta 가 의도적으로 반환) — CWE-200 정책 예외,
//        이름 소스는 meta.dealerName > companyMap (IMP-423), 둘 다 부재 시 raw dealerId 허용.
//      - skMatched / matchedVoucher 경로 (FE cList-scope) — CWE-200 정책 유지,
//        companyMap miss 시 dealerLabel null.
//
// 반환: { lic, name, typeLabel, dealerLabel } | null.
//   - lic: `${name} · ${typeLabel}` 합성 (호환 — 일부 path 가 단일 문자열 표시).
//   - 모든 fallback 부재 (lic + dealerLabel 모두 null) → null.
// IMP-227: param 을 `localLicenseStatusSk` (string) 로 좁혀 호출부 useMemo deps(`localLicenseStatus?.sk`)와 1:1 일치 — stale closure 회귀 표면 제거.
export const VOUCHER_LICENSE_SK_PREFIX = 'lcuse:';

export function resolveOtherLicenseInfo({
    localLicenseStatusSk,
    dealerCustomerLicenses,
    matchedVoucher,
    rayteamsUser,
    companyMap,
    externalVoucherMeta,
    isRenewalLicenseFn,
    t,
}) {
    if (!localLicenseStatusSk) return null;
    if (!localLicenseStatusSk.startsWith(VOUCHER_LICENSE_SK_PREFIX)) return null;

    const skMatched = Array.isArray(dealerCustomerLicenses)
        ? dealerCustomerLicenses.find((v) => v?.sk === localLicenseStatusSk)
        : null;

    // name lookup chain (FEAT-034 우선순위)
    let name = externalVoucherMeta?.groupName || null;
    if (!name && skMatched) {
        name = skMatched.groupName || skMatched.userid || skMatched.email || skMatched.userId || null;
    }
    if (!name && matchedVoucher) {
        // 같은 RAYTeams 사용자의 다른 voucher — groupName 동일 (Confluence invariant).
        // IMP-339: skMatched 분기와 동일하게 userId(camelCase, MongoDB/RAYTeams 소스) fallback 포함.
        name = matchedVoucher.groupName || matchedVoucher.userid || matchedVoucher.email || matchedVoucher.userId || null;
    }
    if (!name) {
        // RAYTeams 로컬 로그인 사용자 (최종 fallback).
        name = rayteamsUser?.name || rayteamsUser?.email || null;
    }

    // typeLabel — skMatched 우선 (정확), fallback sk 5번째 segment.
    let typeLabel = null;
    if (skMatched && typeof isRenewalLicenseFn === 'function') {
        typeLabel = isRenewalLicenseFn(skMatched) ? t('license.label.renewal') : t('license.label.initial');
    } else {
        const parts = localLicenseStatusSk.split(':');
        if (parts.length >= 5) {
            const skType = String(parts[4]).toLowerCase();
            if (skType === 'renewal' || skType === 'renew') typeLabel = t('license.label.renewal');
            else if (skType === 'initial') typeLabel = t('license.label.initial');
        }
    }

    // dealer 2-분기 (CWE-200 정책).
    let dealerLabel = null;
    if (externalVoucherMeta?.dealerId) {
        const extDealerId = externalVoucherMeta.dealerId;
        // IMP-423: BE meta 의 dealerName 우선 — companyMap 은 cList 스코프라 외부 dealer 를
        // 담지 못해 사실상 항상 miss 였고, 그 결과 ID 만 노출됐다 (다른 화면의 "회사명 (ID)"
        // 표기와 불일치). companyMap 조회는 구버전 BE (dealerName 미반환) 대비 fallback 으로 유지.
        const extDealerName = externalVoucherMeta.dealerName
            || companyMap?.[extDealerId]?.name
            || null;
        // FEAT-034: BE 가 의도적으로 반환한 dealerId — 이름 부재 시 raw 허용.
        // IMP-424: 포맷 SSoT(formatCompanyInlineLabel) 사용 — 이름 없으면 raw id 반환(동작 동일).
        dealerLabel = formatCompanyInlineLabel(extDealerId, extDealerName);
    } else {
        const localDealerId = skMatched?.dealerId || matchedVoucher?.dealerId || null;
        const localDealerName = localDealerId
            ? (companyMap?.[localDealerId]?.name || null)
            : null;
        // PR #243 CWE-200: skMatched/matchedVoucher 경로 — companyMap miss 시 null.
        // IMP-424: formatCompanyInlineLabel 은 이름 부재 시 raw id 를 반환하므로(위 분기와 다름),
        // 이 경로의 CWE-200 null 정책을 보존하기 위해 null 폴백을 명시적으로 유지한다.
        dealerLabel = localDealerName ? formatCompanyInlineLabel(localDealerId, localDealerName) : null;
    }

    const lic = (name && typeLabel) ? `${name} · ${typeLabel}` : (name || typeLabel || null);
    if (!lic && !dealerLabel) return null;
    return { lic, name, typeLabel, dealerLabel };
}
